import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { IconAlertCircle, IconPhoto } from '@tabler/icons-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, formatBytes } from '@/lib/utils';
import { looksLikeMaterialGraph } from './materialGraphShape.js';
import { api, type DamJobState } from '../api.js';

// MaterialGraph drags in @xyflow/react (graph runtime + CSS, ~150
// KB minified). Lazy so users browsing texture / blueprint / data
// assets don't pay for it. The shape predicate above is the only
// piece needed synchronously to decide whether to mount.
const MaterialGraph = lazy(() =>
  import('./MaterialGraph.js').then((m) => ({ default: m.MaterialGraph })),
);

// BlueprintPreview shares the @xyflow/react dependency with
// MaterialGraph, so the lazy chunk piggybacks the same vendor split.
// Mounted only when the renderer reports previewKind='graph' for a
// UBlueprint-class asset.
const BlueprintPreview = lazy(() =>
  import('./BlueprintPreview.js').then((m) => ({ default: m.BlueprintPreview })),
);

interface AssetPreviewProps {
  depotPath: string;
  headRev: number;
  filename: string;
  fileSize: number | null;
}

type Kind = 'image' | 'uasset' | 'video' | 'audio' | 'pdf' | '3d';

const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'mpeg', 'm4v', 'ogv', '3gp'];
const AUDIO_EXTS = ['mp3', 'wav', 'flac', 'ogg', 'oga', 'aiff', 'aif', 'au', 'm4a', 'opus'];
const THREED_EXTS = ['fbx', 'obj', 'stl', 'dae', 'ply', '3ds', 'blend', 'x3d', 'glb', 'gltf'];

function classify(filename: string): Kind {
  const lower = filename.toLowerCase();
  const ext = lower.includes('.') ? lower.slice(lower.lastIndexOf('.') + 1) : '';
  if (ext === 'uasset') return 'uasset';
  if (ext === 'pdf') return 'pdf';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  if (THREED_EXTS.includes(ext)) return '3d';
  return 'image';
}

export function AssetPreview(props: AssetPreviewProps) {
  const kind = classify(props.filename);
  switch (kind) {
    case 'image':
      return <ImagePreview {...props} />;
    case 'uasset':
      return <UassetPreview {...props} />;
    case 'video':
      return <VideoPreview {...props} />;
    case 'audio':
      return <AudioPreview {...props} />;
    case 'pdf':
      return <PdfPreview {...props} />;
    case '3d':
      return <ThreeDPreview {...props} />;
  }
}

function UassetPreview(props: AssetPreviewProps) {
  type Kind = 'image' | '3d' | 'graph' | 'properties' | 'none';
  type Phase =
    | { kind: 'loading' }
    | { kind: 'rendering'; jobId: number; state: DamJobState }
    | { kind: 'ready'; previewKind: Kind; hasProperties: boolean }
    | { kind: 'failed'; message: string };

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [tab, setTab] = useState<'preview' | 'properties' | 'graph'>('preview');

  useEffect(() => {
    let cancelled = false;

    async function tick(): Promise<void> {
      const url = `/api/dam/meta?path=${encodeURIComponent(props.depotPath)}&rev=${props.headRev}`;
      const res = await fetch(url, { credentials: 'include' });
      if (cancelled) return;
      if (res.status === 200) {
        const body = (await res.json()) as {
          previewKind?: Kind;
          hasPreviewImage?: boolean;
          hasMeshPreview?: boolean;
          hasProperties?: boolean;
          noPreview?: boolean;
        };
        if (cancelled) return;
        const pk: Kind = body.noPreview
          ? 'none'
          : body.previewKind ??
            (body.hasMeshPreview
              ? '3d'
              : body.hasPreviewImage
                ? 'image'
                : body.hasProperties
                  ? 'properties'
                  : 'none');
        setPhase({
          kind: 'ready',
          previewKind: pk,
          hasProperties: body.hasProperties ?? pk === 'properties',
        });
        return;
      }
      if (res.status === 202) {
        const body = (await res.json()) as { jobId: number; state: DamJobState };
        if (cancelled) return;
        setPhase({ kind: 'rendering', jobId: body.jobId, state: body.state });
        await poll(body.jobId);
        return;
      }
      setPhase({
        kind: 'failed',
        message: `meta status ${res.status}: ${await res.text().catch(() => '')}`,
      });
    }

    async function poll(jobId: number): Promise<void> {
      while (!cancelled) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (cancelled) return;
        try {
          const job = await api.damJob(jobId);
          if (cancelled) return;
          if (job.state === 'done') {
            await tick();
            return;
          }
          if (job.state === 'failed') {
            setPhase({ kind: 'failed', message: job.error ?? 'render failed' });
            return;
          }
          setPhase({ kind: 'rendering', jobId, state: job.state });
        } catch (err) {
          setPhase({
            kind: 'failed',
            message: err instanceof Error ? err.message : String(err),
          });
          return;
        }
      }
    }

    tick().catch((err: unknown) => {
      if (!cancelled) {
        setPhase({
          kind: 'failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [props.depotPath, props.headRev]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') {
    return <ThrobberPanel phase={phase} />;
  }
  if (phase.kind === 'failed') {
    return <FailedPanel message={phase.message} />;
  }
  if (phase.kind === 'ready') {
    const primary =
      phase.previewKind === 'image' ? <ImagePreview {...props} /> :
      phase.previewKind === '3d' ? <ThreeDPreview {...props} /> :
      phase.previewKind === 'graph' ? <BlueprintPreviewLoader {...props} /> :
      phase.previewKind === 'properties' ? <PropertiesPreview {...props} /> :
      <NoPreviewPanel
        filename={props.filename}
        reason="Static meshes / materials / blueprints don't always carry an embedded preview."
      />;

    const looksLikeMaterial =
      /^MI?_/.test(props.filename) ||
      /^MF_/.test(props.filename);

    const showTabs =
      phase.previewKind !== 'none' &&
      phase.previewKind !== 'properties' &&
      (phase.hasProperties || looksLikeMaterial);
    if (!showTabs) return primary;
    return (
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'preview' | 'properties' | 'graph')}>
        <TabsList>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          {phase.hasProperties && <TabsTrigger value="properties">Properties</TabsTrigger>}
          {looksLikeMaterial && <TabsTrigger value="graph">Graph</TabsTrigger>}
        </TabsList>
        <TabsContent value="preview">{primary}</TabsContent>
        {phase.hasProperties && (
          <TabsContent value="properties">
            <PropertiesPreview {...props} />
          </TabsContent>
        )}
        {looksLikeMaterial && (
          <TabsContent value="graph">
            <MaterialGraphTab {...props} />
          </TabsContent>
        )}
      </Tabs>
    );
  }
  return null;
}

// Blueprint primary-preview loader. Mirrors MaterialGraphTab's
// useDamReady → fetch preview.json → render flow, but mounts as the
// modal's primary content (driven by previewKind='graph') rather than
// a side tab. The renderer's BlueprintGraphExporter overwrites
// preview.json with the structured graph DOM for UBlueprint assets,
// so the same `damPreviewUrl(...&ext=json)` URL serves it.
function BlueprintPreviewLoader({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  const [data, setData] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ready = phase.kind === 'ready';
  const renderedAt = phase.kind === 'ready' ? phase.renderedAt : null;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const baseUrl = api.damPreviewUrl(depotPath, headRev, 'json');
    const url = renderedAt
      ? `${baseUrl}&v=${encodeURIComponent(renderedAt)}`
      : baseUrl;
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = (await res.json()) as unknown;
        if (!cancelled) setData(json);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [depotPath, headRev, renderedAt, ready]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;

  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      {error && (
        <div className="p-4">
          <p className="text-sm text-red-400">Failed to load blueprint graph: {error}</p>
        </div>
      )}
      {!error && data === null && (
        <div className="p-4">
          <Spinner />
        </div>
      )}
      {!error && data !== null && (
        <Suspense fallback={<div className="p-4"><Spinner /></div>}>
          <BlueprintPreview data={data} />
        </Suspense>
      )}
    </PreviewShell>
  );
}

function MaterialGraphTab({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  const [data, setData] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ready = phase.kind === 'ready';
  const renderedAt = phase.kind === 'ready' ? phase.renderedAt : null;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const baseUrl = api.damPreviewUrl(depotPath, headRev, 'json');
    const url = renderedAt
      ? `${baseUrl}&v=${encodeURIComponent(renderedAt)}`
      : baseUrl;
    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = (await res.json()) as unknown;
        if (!cancelled) setData(json);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [depotPath, headRev, renderedAt, ready]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;

  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      {error && (
        <div className="p-4">
          <p className="text-sm text-red-400">Failed to load graph: {error}</p>
        </div>
      )}
      {!error && data === null && (
        <div className="p-4">
          <Spinner />
        </div>
      )}
      {!error && data !== null && looksLikeMaterialGraph(data) && (
        <Suspense fallback={<div className="p-4"><Spinner /></div>}>
          <MaterialGraph data={data} />
        </Suspense>
      )}
      {!error && data !== null && !looksLikeMaterialGraph(data) && (
        <div className="p-4 text-xs text-muted-foreground">
          This asset doesn't appear to carry a Material expression graph.
        </div>
      )}
    </PreviewShell>
  );
}

// ---------------------------------------------------------------------------
// Shared readiness hook
// ---------------------------------------------------------------------------

type ReadyPhase =
  | { kind: 'loading' }
  | { kind: 'rendering'; jobId: number; state: DamJobState }
  | { kind: 'ready'; renderedAt: string | null }
  | { kind: 'no-preview' }
  | { kind: 'failed'; message: string };

const POLL_MS = 1000;

function useDamReady(depotPath: string, headRev: number): ReadyPhase {
  const [phase, setPhase] = useState<ReadyPhase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    // Single canonical read against /api/dam/asset-preview. Backend
    // computes phase from job state + meta presence + cache freshness,
    // so the client just renders what it's told. On 'idle' (no job +
    // no meta) we POST to /api/dam/preview to trigger fetch+enqueue,
    // mirroring the previous useDamReady's two-step (probe → enqueue).
    async function tick(): Promise<void> {
      try {
        const preview = await api.damAssetPreview(depotPath, headRev);
        if (cancelled) return;

        switch (preview.phase) {
          case 'ready': {
            const renderedAt =
              typeof preview.meta?.renderedAt === 'string' ? preview.meta.renderedAt : null;
            setPhase({ kind: 'ready', renderedAt });
            return;
          }
          case 'no-preview':
            setPhase({ kind: 'no-preview' });
            return;
          case 'rendering':
            if (preview.job) {
              setPhase({ kind: 'rendering', jobId: preview.job.id, state: preview.job.state });
              await poll(preview.job.id);
            } else {
              setPhase({ kind: 'failed', message: 'rendering phase without job row' });
            }
            return;
          case 'failed':
            setPhase({
              kind: 'failed',
              message: preview.job?.error ?? 'render failed',
            });
            return;
          case 'idle': {
            // No artifact + no in-flight job. Trigger the public preview
            // endpoint to stash + enqueue under the user's p4 ticket
            // (asset-preview is a read-only endpoint by design).
            const enqueueUrl = `/api/dam/preview?path=${encodeURIComponent(
              depotPath,
            )}&rev=${headRev}`;
            const res = await fetch(enqueueUrl, { credentials: 'include' });
            if (cancelled) return;
            if (res.status === 202) {
              const body = (await res.json()) as { jobId: number; state: DamJobState };
              setPhase({ kind: 'rendering', jobId: body.jobId, state: body.state });
              await poll(body.jobId);
              return;
            }
            if (res.status === 200) {
              // Race: rendered between the asset-preview read and this
              // GET. Re-read to pick up the artifact-present state.
              await tick();
              return;
            }
            setPhase({
              kind: 'failed',
              message: `preview enqueue ${res.status}: ${await res.text().catch(() => '')}`,
            });
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }

    async function poll(jobId: number): Promise<void> {
      while (!cancelled) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (cancelled) return;
        try {
          const job = await api.damJob(jobId);
          if (cancelled) return;
          if (job.state === 'done') {
            await tick();
            return;
          }
          if (job.state === 'failed') {
            setPhase({ kind: 'failed', message: job.error ?? 'render failed' });
            return;
          }
          setPhase({ kind: 'rendering', jobId, state: job.state });
        } catch (err) {
          setPhase({
            kind: 'failed',
            message: err instanceof Error ? err.message : String(err),
          });
          return;
        }
      }
    }

    tick();

    return () => {
      cancelled = true;
    };
  }, [depotPath, headRev]);

  return phase;
}

// ---------------------------------------------------------------------------
// Shared UI shells
// ---------------------------------------------------------------------------

function PreviewShell({
  filename,
  fileSize,
  rawHref,
  children,
  className,
}: {
  filename: string;
  fileSize: number | null;
  rawHref: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-neutral-800 bg-card',
        className,
      )}
    >
      <div className="flex flex-nowrap items-center justify-between border-b border-neutral-800 bg-neutral-900 px-4 py-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <IconPhoto size={14} />
          <span className="font-mono">{filename}</span>
          {fileSize !== null && <span>· {formatBytes(fileSize)}</span>}
        </span>
        <a
          href={rawHref}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-sm border border-neutral-800 bg-card px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Raw
        </a>
      </div>
      {children}
    </div>
  );
}

function ThrobberPanel({ phase }: { phase: ReadyPhase }) {
  if (phase.kind !== 'loading' && phase.kind !== 'rendering') return null;
  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-md border border-neutral-800 bg-card p-8">
      <Spinner />
      <span className="text-sm text-muted-foreground">
        {phase.kind === 'loading'
          ? 'Fetching preview…'
          : `Rendering preview… (${phase.state}; job #${phase.jobId})`}
      </span>
    </div>
  );
}

function NoPreviewPanel({ filename, reason }: { filename: string; reason?: string }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-card p-8 text-center text-muted-foreground">
      <IconPhoto size={32} className="mx-auto mb-2 opacity-40" />
      <p className="text-sm">
        No preview available for <strong className="font-mono">{filename}</strong>.
      </p>
      {reason && <p className="mt-1 text-xs">{reason}</p>}
    </div>
  );
}

function FailedPanel({ message }: { message: string }) {
  return (
    <Alert tone="destructive">
      <IconAlertCircle />
      <AlertTitle>Preview failed</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Image / .uasset previews — blob URL + zoom-on-click
// ---------------------------------------------------------------------------

function ImagePreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  type Phase =
    | { kind: 'loading' }
    | { kind: 'rendering'; jobId: number; state: DamJobState }
    | { kind: 'ready'; url: string }
    | { kind: 'no-preview' }
    | { kind: 'failed'; message: string };

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [zoomed, setZoomed] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  const previewUrl = useMemo(
    () => api.damPreviewUrl(depotPath, headRev),
    [depotPath, headRev],
  );

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchPreview(): Promise<void> {
      const res = await fetch(previewUrl, { credentials: 'include' });
      if (cancelled) return;
      const ct = res.headers.get('content-type') ?? '';
      if (res.status === 200 && ct.startsWith('image/')) {
        const blob = await res.blob();
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setPhase({ kind: 'ready', url });
        return;
      }
      if (res.status === 200 && ct.includes('json')) {
        setPhase({ kind: 'no-preview' });
        return;
      }
      if (res.status === 202) {
        const body = (await res.json()) as { jobId: number; state: DamJobState };
        if (cancelled) return;
        setPhase({ kind: 'rendering', jobId: body.jobId, state: body.state });
        await pollUntilDone(body.jobId);
        return;
      }
      setPhase({
        kind: 'failed',
        message: `preview status ${res.status}: ${await res.text().catch(() => '')}`,
      });
    }

    async function pollUntilDone(jobId: number): Promise<void> {
      while (!cancelled) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (cancelled) return;
        try {
          const job = await api.damJob(jobId);
          if (cancelled) return;
          if (job.state === 'done') {
            await fetchPreview();
            return;
          }
          if (job.state === 'failed') {
            setPhase({ kind: 'failed', message: job.error ?? 'render failed' });
            return;
          }
          setPhase({ kind: 'rendering', jobId, state: job.state });
        } catch (err) {
          setPhase({
            kind: 'failed',
            message: err instanceof Error ? err.message : String(err),
          });
          return;
        }
      }
    }

    fetchPreview().catch((err: unknown) => {
      if (!cancelled) {
        setPhase({
          kind: 'failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    });

    return () => {
      cancelled = true;
      // Revoke any blob URL the cancelled effect leaves behind. The
      // unmount cleanup above also revokes, but on a previewUrl
      // change (rev bump, path swap) only this cleanup runs — the
      // unmount handler doesn't fire until the component fully
      // leaves the tree.
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [previewUrl]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') {
    return <ThrobberPanel phase={phase} />;
  }
  if (phase.kind === 'failed') {
    return <FailedPanel message={phase.message} />;
  }
  if (phase.kind === 'no-preview') {
    return (
      <NoPreviewPanel
        filename={filename}
        reason="Static meshes and materials get thumbnails only when the UE editor saves one."
      />
    );
  }

  return (
    <>
      <PreviewShell
        filename={filename}
        fileSize={fileSize}
        rawHref={api.fileUrl(depotPath)}
      >
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="block w-full cursor-zoom-in border-0 bg-transparent p-0"
        >
          <img
            src={phase.url}
            alt={filename}
            className="mx-auto block max-h-[70vh] max-w-full object-contain"
            style={{
              background:
                'repeating-conic-gradient(#1a1f29 0% 25%, #0d1117 0% 50%) 50% / 16px 16px',
            }}
          />
        </button>
      </PreviewShell>

      {/* Image-zoom overlay: now a Radix Dialog so Escape dismisses
          + focus is trapped + role="dialog" announces it correctly.
          DialogContent's default chrome (small panel) doesn't suit a
          fullscreen image view, so we override sizing + remove the
          built-in close to keep the click-anywhere-to-close affordance. */}
      <Dialog open={zoomed} onOpenChange={setZoomed}>
        <DialogContent
          hideClose
          aria-describedby={undefined}
          onClick={() => setZoomed(false)}
          className="max-h-[95vh] max-w-[95vw] cursor-zoom-out border-0 bg-transparent p-6 shadow-none"
        >
          <DialogTitle className="sr-only">{filename}</DialogTitle>
          <img
            src={phase.url}
            alt={filename}
            className="max-h-full max-w-full object-contain"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Video, audio, PDF, 3D
// ---------------------------------------------------------------------------

function VideoPreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;
  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      <video
        src={api.damPreviewUrl(depotPath, headRev)}
        controls
        className="block max-h-[70vh] w-full bg-black"
      />
    </PreviewShell>
  );
}

function AudioPreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<{ destroy: () => void } | null>(null);
  const [wsError, setWsError] = useState<string | null>(null);

  useEffect(() => {
    if (phase.kind !== 'ready' || !containerRef.current) return;
    let cancelled = false;
    import('wavesurfer.js')
      .then(async ({ default: WaveSurfer }) => {
        if (cancelled || !containerRef.current) return;
        const ws = WaveSurfer.create({
          container: containerRef.current,
          waveColor: '#a5d6ff',
          progressColor: '#79c0ff',
          cursorColor: '#e6edf3',
          barWidth: 2,
          barGap: 1,
          barRadius: 1,
          height: 96,
          url: api.damPreviewUrl(depotPath, headRev),
        });
        wsRef.current = ws;
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setWsError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
      wsRef.current?.destroy();
      wsRef.current = null;
    };
  }, [phase.kind, depotPath, headRev]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;

  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      <div className="p-4">
        {wsError ? (
          <div className="flex items-center gap-2">
            <IconAlertCircle size={16} className="text-red-400" />
            <span className="text-xs text-red-400">waveform failed: {wsError}</span>
          </div>
        ) : (
          <div ref={containerRef} />
        )}
        <audio
          src={api.damPreviewUrl(depotPath, headRev)}
          controls
          className="mt-2 w-full"
        />
      </div>
    </PreviewShell>
  );
}

function PdfPreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;
  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      <iframe
        title={filename}
        src={api.damPreviewUrl(depotPath, headRev)}
        className="block h-[70vh] w-full border-0 bg-white"
      />
    </PreviewShell>
  );
}

// model-viewer's built-in environments shipped under
// `node_modules/@google/model-viewer/lib/assets/`. "legacy" is the older
// default — moderately directional, gives clear specular highlights on
// metallic/glossy surfaces without coloring matte materials. "neutral"
// is flatter and more color-accurate but makes everything look like
// it's lit by an overcast sky, which obscures roughness variation.
// We prefer "legacy" for asset previews where seeing the material
// response matters more than absolute color fidelity.
const ENV_IMAGE = 'legacy';

// 0.4 calibrates against the `legacy` environment + Khronos PBR Neutral
// tone map combination — both make incoming light read brighter than
// model-viewer's documented 1.0 default would suggest. Empirically
// tuned: 1.0 blew out highlights on the Mentice TEE_Probe corpus and
// most assets needed the slider dropped to ~0.35 to feel right; 0.4
// starts at "natural" and lets the slider tune up or down.
const DEFAULT_EXPOSURE = 0.4;

// Khronos PBR Neutral is the most-faithful tone map for inspecting
// authored PBR materials — preserves albedo and roughness response
// without filmic crushing of highlights or shadows. Spec:
// https://github.com/KhronosGroup/ToneMapping
const TONE_MAPPING = 'khronos-pbr-neutral';

// Subtle ground shadow — anchors the asset in space without competing
// with the material's own specular response.
const SHADOW_INTENSITY = 0.6;

function ThreeDPreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const phase = useDamReady(depotPath, headRev);
  const loaded = useRef(false);
  const mvRef = useRef<HTMLElement | null>(null);
  const [exposure, setExposure] = useState(DEFAULT_EXPOSURE);
  const [wireframe, setWireframe] = useState(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    import('@google/model-viewer').catch(() => {
      loaded.current = false;
    });
  }, []);

  // <model-viewer> doesn't expose a first-class wireframe API. Walk the
  // underlying three.js scene on every (re)load and flip wireframe on
  // each material. Stable across model-viewer v1 releases.
  //
  // Dep list deliberately uses `phase.kind` and the `renderedAt`
  // string instead of the whole `phase` object — `useDamReady`
  // returns a fresh object each render, so depending on `phase`
  // would re-run + re-attach this listener every render even when
  // the meaningful state hadn't changed.
  const phaseRenderedAt = phase.kind === 'ready' ? phase.renderedAt : null;
  useEffect(() => {
    const mv = mvRef.current;
    if (!mv) return;
    const apply = (): void => {
      const sym = Object.getOwnPropertySymbols(mv).find((s) => s.description === 'scene');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const scene: any = sym ? (mv as any)[sym] : null;
      if (!scene?.traverse) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      scene.traverse((node: any) => {
        const mat = node?.material;
        if (!mat) return;
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if ('wireframe' in m) m.wireframe = wireframe;
          m.needsUpdate = true;
        }
      });
    };
    mv.addEventListener('load', apply);
    apply();
    return () => mv.removeEventListener('load', apply);
  }, [wireframe, phase.kind, phaseRenderedAt]);

  if (phase.kind === 'loading' || phase.kind === 'rendering') return <ThrobberPanel phase={phase} />;
  if (phase.kind === 'failed') return <FailedPanel message={phase.message} />;
  if (phase.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;

  const baseUrl = api.damPreviewUrl(depotPath, headRev);
  const src = phase.renderedAt
    ? `${baseUrl}&v=${encodeURIComponent(phase.renderedAt)}`
    : baseUrl;

  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      <div className="flex w-full flex-col">
        <div className="flex flex-wrap items-center gap-4 border-b border-neutral-800 bg-neutral-900 px-3 py-2">
          <div className="flex min-w-[200px] items-center gap-2">
            <span className="text-xs text-muted-foreground">Exposure</span>
            <Slider
              min={0.25}
              max={2}
              step={0.05}
              value={exposure}
              onChange={(e) => setExposure(parseFloat(e.currentTarget.value))}
              className="min-w-[120px] flex-1"
            />
            <span className="w-10 text-right font-mono text-[11px] text-muted-foreground">
              {exposure.toFixed(2)}
            </span>
          </div>
          <label className="flex items-center gap-2">
            <Switch checked={wireframe} onCheckedChange={setWireframe} />
            <span className="text-xs">Wireframe</span>
          </label>
        </div>
        <div className="h-[70vh] w-full bg-card">
          <model-viewer
            ref={mvRef as React.RefObject<HTMLElement>}
            src={src}
            camera-controls
            auto-rotate
            ar-status="not-presenting"
            environment-image={ENV_IMAGE}
            exposure={exposure}
            tone-mapping={TONE_MAPPING}
            shadow-intensity={SHADOW_INTENSITY}
            style={{ width: '100%', height: '100%', background: 'var(--color-card)' }}
          />
        </div>
      </div>
    </PreviewShell>
  );
}

// Collapsible JSON tree viewer used for non-mesh, non-image .uasset
// previews. Render strategy is bounded to keep the initial paint
// cheap even on multi-MB data assets:
//   - Default-open depth is 1 (only the root container expands).
//     Was depth<2, which auto-expanded the first two layers and on
//     external-actor blobs mounted tens of thousands of nodes per
//     render.
//   - Containers with more than CHILDREN_PAGE_SIZE entries paginate.
//     First page renders immediately; subsequent pages on demand
//     via a "Show next N" button.
const DEFAULT_OPEN_DEPTH = 1;
const CHILDREN_PAGE_SIZE = 100;

function JsonNode({ name, value, depth = 0 }: { name?: string; value: unknown; depth?: number }) {
  const [open, setOpen] = useState(depth < DEFAULT_OPEN_DEPTH);
  const [pages, setPages] = useState(1);

  if (value === null) return <Leaf name={name} type="null" text="null" />;
  if (typeof value === 'string')
    return <Leaf name={name} type="string" text={`"${value}"`} />;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint')
    return <Leaf name={name} type={typeof value} text={String(value)} />;

  if (Array.isArray(value)) {
    const total = value.length;
    const shown = Math.min(total, pages * CHILDREN_PAGE_SIZE);
    return (
      <div className={cn(depth === 0 ? '' : 'ml-4')}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="cursor-pointer select-none bg-transparent text-left text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {open ? '▾' : '▸'}{' '}
          {name !== undefined && <span className="text-theia-300">{name}: </span>}
          <span className="text-muted-foreground">[{total}]</span>
        </button>
        {open && (
          <>
            {value.slice(0, shown).map((v, i) => (
              <JsonNode key={i} name={String(i)} value={v} depth={depth + 1} />
            ))}
            {shown < total && (
              <ShowMoreRow
                remaining={total - shown}
                onShowMore={() => setPages((p) => p + 1)}
              />
            )}
          </>
        )}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const total = entries.length;
    const shown = Math.min(total, pages * CHILDREN_PAGE_SIZE);
    return (
      <div className={cn(depth === 0 ? '' : 'ml-4')}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="cursor-pointer select-none bg-transparent text-left text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {open ? '▾' : '▸'}{' '}
          {name !== undefined && <span className="text-theia-300">{name}: </span>}
          <span className="text-muted-foreground">{`{${total}}`}</span>
        </button>
        {open && (
          <>
            {entries.slice(0, shown).map(([k, v]) => (
              <JsonNode key={k} name={k} value={v} depth={depth + 1} />
            ))}
            {shown < total && (
              <ShowMoreRow
                remaining={total - shown}
                onShowMore={() => setPages((p) => p + 1)}
              />
            )}
          </>
        )}
      </div>
    );
  }

  return <Leaf name={name} type="unknown" text={String(value)} />;
}

function ShowMoreRow({
  remaining,
  onShowMore,
}: {
  remaining: number;
  onShowMore: () => void;
}) {
  const next = Math.min(remaining, CHILDREN_PAGE_SIZE);
  return (
    <div className="ml-4">
      <button
        type="button"
        onClick={onShowMore}
        className="cursor-pointer bg-transparent text-left text-theia-400 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        … show next {next.toLocaleString()} of {remaining.toLocaleString()}
      </button>
    </div>
  );
}

function Leaf({ name, type, text }: { name?: string | undefined; type: string; text: string }) {
  const color =
    type === 'string' ? 'text-sky-300'
    : type === 'number' ? 'text-orange-300'
    : type === 'boolean' ? 'text-violet-300'
    : type === 'null' ? 'text-muted-foreground'
    : 'text-foreground';
  return (
    <div className="ml-4">
      {name !== undefined && <span className="text-theia-300">{name}: </span>}
      <span className={color}>{text}</span>
    </div>
  );
}

// Size thresholds for the properties.json gate. External-actor
// `.uasset` files produce 60-100 MB JSON dumps; auto-loading +
// parsing them blocks the browser main thread for several seconds.
// Below 1 MB: auto-load (no UX change for typical material/
// blueprint assets). 1-50 MB: warning + opt-in. > 50 MB: in-page
// rendering would lock the tab even with bounded JsonNode, so we
// only offer the raw download path.
const PROPERTIES_AUTOLOAD_MAX_BYTES = 1 * 1024 * 1024;
const PROPERTIES_INPAGE_MAX_BYTES = 50 * 1024 * 1024;

type PropertiesPhase =
  | { kind: 'sizing' }
  | { kind: 'gated'; bytes: number | null }
  | { kind: 'too-big'; bytes: number }
  | { kind: 'loading'; bytes: number | null }
  | { kind: 'ready'; data: unknown; bytes: number | null }
  | { kind: 'failed'; message: string };

function PropertiesPreview({ depotPath, headRev, filename, fileSize }: AssetPreviewProps) {
  const meta = useDamReady(depotPath, headRev);
  const [phase, setPhase] = useState<PropertiesPhase>({ kind: 'sizing' });

  const renderedAt = meta.kind === 'ready' ? meta.renderedAt : null;
  const ready = meta.kind === 'ready';

  const baseUrl = api.damPreviewUrl(depotPath, headRev, 'json');
  const url = renderedAt ? `${baseUrl}&v=${encodeURIComponent(renderedAt)}` : baseUrl;

  // Step 1: HEAD-request the JSON to discover its size before
  // committing to a multi-megabyte transfer + parse. The backend
  // serves preview.json via fs.createReadStream which sets
  // Content-Length, so HEAD gives us the size for free.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setPhase({ kind: 'sizing' });
    // `cache: 'no-store'` is load-bearing: the HEAD response is served with
    // `max-age=300`, so the browser can replay a stale cached HEAD — and if
    // that cached entry was captured during a transient window where
    // x-preview-size was absent, every later probe reuses the headerless
    // copy and the size reads as unknown. Bypass the cache so the probe
    // always reflects the live response (which carries the header).
    fetch(url, { method: 'HEAD', credentials: 'include', cache: 'no-store' })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setPhase({ kind: 'failed', message: `HEAD status ${res.status}` });
          return;
        }
        // x-preview-size: custom header set by the backend HEAD
        // handler. Fastify rewrites Content-Length on empty-body
        // responses to 0, so we can't rely on it here.
        const sizeHeader =
          res.headers.get('x-preview-size') ?? res.headers.get('content-length');
        const bytes = sizeHeader ? parseInt(sizeHeader, 10) : NaN;
        // Triage by size. The key change from a naive impl: if
        // HEAD didn't return a usable Content-Length, gate rather
        // than auto-load. A missing length can mean the response
        // was chunked-encoded or proxied through something that
        // stripped the header — we don't know whether it's a 1 KB
        // texture metadata blob or a 100 MB external-actor dump,
        // and the worst case is the one that locks the tab.
        if (!Number.isFinite(bytes)) {
          // HEAD returned 200 but no usable size (header stripped, a
          // proxy/cache served a headerless 200, or a mid-rerender race).
          // Gate with an unknown size rather than misreporting "0 B".
          setPhase({ kind: 'gated', bytes: null });
        } else if (bytes <= PROPERTIES_AUTOLOAD_MAX_BYTES) {
          setPhase({ kind: 'loading', bytes });
          void loadFull(bytes);
        } else if (bytes <= PROPERTIES_INPAGE_MAX_BYTES) {
          setPhase({ kind: 'gated', bytes });
        } else {
          setPhase({ kind: 'too-big', bytes });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPhase({
            kind: 'failed',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });

    async function loadFull(bytes: number | null): Promise<void> {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = (await res.json()) as unknown;
        if (!cancelled) setPhase({ kind: 'ready', data: json, bytes });
      } catch (err) {
        if (!cancelled) {
          setPhase({
            kind: 'failed',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    }

    return () => {
      cancelled = true;
    };
  }, [url, ready]);

  if (meta.kind === 'loading' || meta.kind === 'rendering') return <ThrobberPanel phase={meta} />;
  if (meta.kind === 'failed') return <FailedPanel message={meta.message} />;
  if (meta.kind === 'no-preview') return <NoPreviewPanel filename={filename} />;

  return (
    <PreviewShell filename={filename} fileSize={fileSize} rawHref={api.fileUrl(depotPath)}>
      <div className="max-h-[70vh] overflow-auto bg-card p-3 font-mono text-xs">
        {phase.kind === 'failed' && (
          <span className="text-sm text-red-400">
            Failed to load properties: {phase.message}
          </span>
        )}
        {(phase.kind === 'sizing' || (phase.kind === 'loading' && phase.bytes === null)) && (
          <Spinner />
        )}
        {phase.kind === 'gated' && (
          <PropertiesGate
            bytes={phase.bytes}
            onLoad={() => {
              setPhase({ kind: 'loading', bytes: phase.bytes });
              void (async () => {
                try {
                  const res = await fetch(url, { credentials: 'include' });
                  if (!res.ok) throw new Error(`status ${res.status}`);
                  const json = (await res.json()) as unknown;
                  setPhase({ kind: 'ready', data: json, bytes: phase.bytes });
                } catch (err) {
                  setPhase({
                    kind: 'failed',
                    message: err instanceof Error ? err.message : String(err),
                  });
                }
              })();
            }}
          />
        )}
        {phase.kind === 'too-big' && (
          <PropertiesTooBig bytes={phase.bytes} rawUrl={url} />
        )}
        {phase.kind === 'loading' && phase.bytes !== null && (
          <div className="flex items-center gap-2">
            <Spinner />
            <span className="text-xs text-muted-foreground">
              Parsing {formatBytes(phase.bytes)} of properties…
            </span>
          </div>
        )}
        {phase.kind === 'ready' && <JsonNode value={phase.data} />}
      </div>
    </PreviewShell>
  );
}

function PropertiesGate({
  bytes,
  onLoad,
}: {
  bytes: number | null;
  onLoad: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-2 py-3 text-sm">
      <div className="text-foreground">
        {bytes === null ? (
          <>Properties dump size couldn&apos;t be determined. Rendering it inline will fetch + parse the full blob.</>
        ) : (
          <>
            Properties dump is{' '}
            <strong className="font-mono">{formatBytes(bytes)}</strong>. Rendering
            it inline will fetch + parse the full blob.
          </>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        Default-collapsed view + page-limited child rendering keeps the tree
        responsive once loaded.
      </div>
      <button
        type="button"
        onClick={onLoad}
        className="mt-1 inline-flex items-center gap-1 rounded-sm border border-neutral-700 bg-neutral-800 px-3 py-1 text-xs text-foreground hover:bg-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Load anyway
      </button>
    </div>
  );
}

function PropertiesTooBig({
  bytes,
  rawUrl,
}: {
  bytes: number;
  rawUrl: string;
}) {
  return (
    <div className="flex flex-col items-start gap-2 py-3 text-sm">
      <div className="text-foreground">
        Properties dump is{' '}
        <strong className="font-mono">{formatBytes(bytes)}</strong> — too large
        to render in the browser without locking the tab.
      </div>
      <div className="text-xs text-muted-foreground">
        External-actor <code>.uasset</code> files commonly hit this size when
        a level has thousands of placed actors. Download the raw JSON to
        inspect with a streaming parser locally.
      </div>
      <a
        href={rawUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1 rounded-sm border border-neutral-700 bg-neutral-800 px-3 py-1 text-xs text-foreground hover:bg-neutral-700"
      >
        View raw JSON ↗
      </a>
    </div>
  );
}


// JSX types for the <model-viewer> Web Component. React 19 reads JSX
// intrinsics from `React.JSX`, not the older `global.JSX`.
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          src?: string;
          alt?: string;
          'camera-controls'?: boolean;
          'auto-rotate'?: boolean;
          'ar-status'?: string;
          'environment-image'?: string | undefined;
          exposure?: string | number | undefined;
          'tone-mapping'?: string | undefined;
          'shadow-intensity'?: string | number | undefined;
        },
        HTMLElement
      >;
    }
  }
}
